import posthog from "posthog-js";
import {
  data,
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useHref,
  useNavigate,
  type LoaderFunctionArgs,
} from "react-router";
import { toast as notify, ToastContainer } from "react-toastify";
import toastStyles from "react-toastify/ReactToastify.css?url";
import { getToast } from "remix-toast";

import { StrictMode, Suspense, useEffect } from "react";
import type { Route } from "./+types/root";
import { getAdminFromSession } from "./.server/lib/admin/admin-auth";
import { ENV } from "./.server/lib/env";
import { getSuperIds } from "./.server/lib/posthog/init.server";
import { getUserFromSession } from "./.server/lib/session";
import Spinner from "./components/Spinner";
import { usePosthogDistinctIdSync } from "./hooks/posthog/usePosthogDistinctIdSync";
import { usePosthogPageView } from "./hooks/posthog/usePosthogPageViews";
import { useWSConnection } from "./hooks/useWSConnection";
// Stylesheets
import { RouterProvider } from "react-aria";
import stylesheet from "./styles/app.css?url";
import fonts from "./styles/fonts.css?url";
declare global {
  interface Window {
    env: ReturnType<typeof getWindowEnv>;
  }
}

function getWindowEnv() {
  // The following env vars are made available to the client bundle
  return {
    VALDERA_ENV: ENV.VALDERA_ENV,
    APP_URL: ENV.APP_URL,
    POSTHOG_KEY: ENV.POSTHOG_KEY,
    POSTHOG_HOST: ENV.POSTHOG_HOST,
    GOOGLE_MAPS_API_KEY: ENV.GOOGLE_MAPS_API_KEY,
  } as const;
}

export async function loader({ request }: LoaderFunctionArgs) {
  const { toast, headers } = await getToast(request);

  const env = getWindowEnv();
  const user = await getUserFromSession(request);
  const admin = await getAdminFromSession(request);
  const { distinctId } = await getSuperIds(request);

  return data({ toast, env, user, admin, distinctId }, { headers });
}

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
  {
    rel: "apple-touch-icon",
    sizes: "180x180",
    href: "/images/apple-touch-icon.png",
  },
  {
    rel: "icon",
    type: "image/png",
    sizes: "32x32",
    href: "/images/favicon-32x32.png",
  },
  { rel: "stylesheet", href: fonts },
  { rel: "stylesheet", href: stylesheet },
  { rel: "stylesheet", href: toastStyles },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {/*
          RouterProvider enables React Aria components to work with Remix routing.
          This allows components like <MenuItem href="/foo"> to navigate using
          client-side routing instead of full page reloads, similar to how
          React Router's <Link> component works.
          See: https://react-spectrum.adobe.com/react-aria/routing.html#remix
        */}
        {/* eslint-disable-next-line react-hooks/react-compiler */}
        <RouterProvider navigate={navigate} useHref={useHref}>
          {children}
          <ScrollRestoration />
          <Scripts />
          <ToastContainer />
        </RouterProvider>
      </body>
    </html>
  );
}

export function meta() {
  return [{ title: "Valdera" }];
}

export default function App({ loaderData }: Route.ComponentProps) {
  const { toast, user, env } = loaderData;
  useEffect(() => {
    posthog.init(env.POSTHOG_KEY, {
      api_host: env.POSTHOG_HOST,
      person_profiles: "always",
    });
  }, [env.POSTHOG_HOST, env.POSTHOG_KEY]);
  useEffect(() => {
    if (toast) {
      notify(toast.message, { type: toast.type });
    }
  }, [toast]);

  useWSConnection({ user });
  usePosthogDistinctIdSync();
  usePosthogPageView();
  return (
    <StrictMode>
      <script
        dangerouslySetInnerHTML={{
          __html: `window.env = ${JSON.stringify(env)}`,
        }}
      />
      <Suspense fallback={<Spinner />}>
        <Outlet />
      </Suspense>
    </StrictMode>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  const isNonProd = import.meta.env.DEV || window?.env?.VALDERA_ENV != "prod";

  if (isRouteErrorResponse(error)) {
    message = error.statusText || String(error.status) || "Unknown Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.data || details;
  } else if (isNonProd && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="container mx-auto p-4 pt-16">
      <h1>{message}</h1>
      <p>{typeof details == "object" ? JSON.stringify(details) : details}</p>
      {stack && (
        <pre className="w-full overflow-x-auto p-4">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
